// FieldIQ — Formatting helpers, streak badges + per-facility RAG widget config

const safeCssColor = (val) =>
  /^#[0-9a-fA-F]{3,8}$/.test(val) ? val : "#000000";

export const parseMessageText = (text) => {
  const urlPattern = /(https?:\/\/[^\s<>"')\]]+)/g;
  urlPattern.lastIndex = 0;
  const segments = text.split(urlPattern);
  return segments.map((seg, i) =>
    /^https?:\/\//.test(seg)
      ? { type: "link", value: seg, key: i }
      : { type: "text", value: seg, key: i },
  );
};

export const getUserInitials = (user) => {
  if (!user) return "U";
  const f = user.first_name?.[0] || "";
  const l = user.last_name?.[0] || "";
  return (f + l).toUpperCase() || user.name?.[0]?.toUpperCase() || "U";
};

// ═══════════════════════════════════════════════════════════════════
// RAG CONFIG — reads from the same localStorage key as Client Portal
// so any customizations set there appear here automatically
// ═══════════════════════════════════════════════════════════════════

export const DEFAULT_RAG_COLORS = {
  bg: "#0d1b2e",
  sidebar: "#152338",
  chatBg: "#1e3352",
  accent: "#4a6fa5",
  text: "#8dafd8",
  headings: "#e8eef4",
  userBubble: "#243d63",
  botBubble: "#1e3352",
};

export const DEFAULT_QUICK_PROMPTS = {
  General: [
    "What documents do you have available?",
    "Summarize the key topics in this knowledge base",
    "What are the most recent updates?",
  ],
  "Deep Dive": [
    "Explain the main policies in detail",
    "What are the compliance requirements?",
    "Show me related documents on this topic",
  ],
};

/**
 * The local calendar date of an instant, as YYYY-MM-DD.
 *
 * Do not reach for `iso.slice(0, 10)`: event timestamps carry a UTC offset, so
 * slicing yields the UTC date. A 7pm Phoenix event is 02:00 UTC the following
 * day, and the string would say tomorrow while the time beside it said 7pm.
 */
export const localYmd = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const formatActivityDate = (dateStr) => {
  if (!dateStr) return "Date TBD";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return "Date TBD";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 0 && diff < 7)
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const getRagConfig = (facilityId) => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(`hyve-rag-config-${facilityId}`) || "{}",
    );
    const config = {};
    if (raw.colors && typeof raw.colors === "object") {
      config.colors = {};
      for (const [k, v] of Object.entries(raw.colors)) {
        if (typeof v === "string") config.colors[k] = safeCssColor(v);
      }
    }
    if (
      raw.topics &&
      typeof raw.topics === "object" &&
      !Array.isArray(raw.topics)
    ) {
      config.topics = {};
      for (const [cat, prompts] of Object.entries(raw.topics)) {
        if (typeof cat === "string" && Array.isArray(prompts)) {
          config.topics[cat] = prompts.filter((p) => typeof p === "string");
        }
      }
    }
    const safeUrl = (val) => {
      if (typeof val !== "string" || !val) return "";
      try {
        return new URL(val).href;
      } catch {
        return "";
      }
    };
    const safeInt = (val, min, max, fallback) => {
      const n = parseInt(val, 10);
      return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
    };
    if (raw.logoUrl) config.logoUrl = safeUrl(raw.logoUrl);
    if (raw.botAvatarUrl) config.botAvatarUrl = safeUrl(raw.botAvatarUrl);
    if (raw.borderRadius)
      config.borderRadius = safeInt(raw.borderRadius, 0, 32, 12);
    if (raw.fontSize) config.fontSize = safeInt(raw.fontSize, 10, 20, 14);
    return config;
  } catch {
    return {};
  }
};

// ═══════════════════════════════════════════════════════════════════
// CONVERSATION PERSISTENCE
// ═══════════════════════════════════════════════════════════════════

// Returns the emoji string and day label for a given streak count.
// Days 1–4: 🔥, +1 fire per 5 days up to 5 fires (day 20–24).
// Day 25+: switches to 🏆 (single trophy, no fires).
export const streakBadge = (streak = 1) => {
  const label = `Day ${streak}`;
  if (streak >= 25) return { emojis: "🏆", label };
  const fires = Math.min(Math.floor(streak / 5) + 1, 5);
  return { emojis: "🔥".repeat(fires), label };
};

export const interactionBadge = (total = 0) => {
  if (total <= 0) return null;
  if (total > 25) return { emojis: "🏆", label: `${total} interactions` };
  const count = Math.ceil(total / 5);
  return {
    emojis: "🎯".repeat(count),
    label: `${total} interaction${total !== 1 ? "s" : ""}`,
  };
};

// Parse a facility_id slug from the URL path, e.g. /AZLD4 → "AZLD4".
// Ignores paths that look like static assets or known app routes.
// Shared by App.js (facility selection) and index.js (early manifest swap).
export const parseFacilitySlug = () => {
  const seg = window.location.pathname.replace(/^\//, "").split("/")[0];
  if (!seg || seg === "index.html") return null;
  // Facility IDs are 2–20 uppercase alphanumeric/underscore chars
  return /^[A-Z0-9_]{2,20}$/.test(seg.toUpperCase()) ? seg.toUpperCase() : null;
};
